import { api } from "./api";

/** §v3.product-gallery (2026-05-11) — one entry in a product's
 *  image gallery. The server stores these as a JSON array on the
 *  ``products`` row and the dashboard / chat agent both consume
 *  them through the ``images`` field on a Product.
 *
 *  Invariants enforced by the server:
 *    - exactly one entry has ``primary: true`` (the rest are false)
 *    - ``sort_order`` is contiguous 0..N-1 in iteration order
 *    - the entry marked ``primary`` is mirrored to the legacy
 *      single-image ``image_url`` column so older clients keep
 *      seeing the right thumbnail.
 */
export interface ProductImage {
  url: string;
  alt: string;
  primary: boolean;
  sort_order: number;
}

export interface ProductVariant {
  /** Human label shown in the UI ("50 ml", "Large", "Vanilla"). */
  label: string;
  /** Size / weight string (mirrors net_weight format). */
  size: string;
  price_aed: string;
  sku: string;
  available: boolean;
}

export interface Product {
  product_id: string | number;
  product_name: string;
  category: string;
  product_type: string;
  price_aed: string;
  net_weight: string;
  description_short: string;
  /** §v3.products-long-desc (2026-05-11) — markdown-style body.
   *  Read by the voice agent (prefers this over the short
   *  description). Dashboard edits propagate live via the
   *  catalog cache-invalidate hook. */
  description_long: string;
  website_url: string;
  image_url: string;
  // §v3.inventory (2026-05-08) — manual stock tracking. The
  // backend returns these as numbers; older deployments without
  // the migration applied will return 0/10 defaults so the chip
  // rendering still has a value to show.
  stock_count: number;
  low_stock_threshold: number;
  // §v3.product-gallery (2026-05-11) — multi-image gallery. The
  // primary entry's URL also lands in ``image_url`` for legacy
  // callers; new code should read ``images`` directly so it can
  // show a thumbnail strip + send multiple shots to chat.
  images: ProductImage[];
  /** §v3.kb-scrape-variants (2026-05-11) — multi-size / multi-
   *  weight variants. Empty list for single-SKU products. */
  variants: ProductVariant[];
  /** §v3.bundles (2026-05-11) — for bundle products only. List
   *  of component refs {product_id, qty}. Empty for individual
   *  SKUs. The voice agent's bundle recommender reads from this. */
  bundle_components: Array<{ product_id: number; qty: number }>;
  [key: string]: unknown;
}

export interface ProductUpdate {
  product_name?: string;
  category?: string;
  product_type?: string;
  price_aed?: string;
  net_weight?: string;
  description_short?: string;
  /** §v3.products-long-desc (2026-05-11) */
  description_long?: string;
  website_url?: string;
  image_url?: string;
  stock_count?: number;
  low_stock_threshold?: number;
}

// §v3.products-crud (2026-05-08) — payload for POST /api/products.
// product_name is the only required field; product_id is optional
// (server auto-allocates max+1 when omitted). Other fields default
// to empty string on the server side, so the operator can ship a
// minimal "just the name" entry and fill the rest in via the
// edit drawer later.
export interface ProductCreate {
  product_name: string;
  category?: string;
  product_type?: string;
  price_aed?: string;
  net_weight?: string;
  description_short?: string;
  /** §v3.products-long-desc (2026-05-11) */
  description_long?: string;
  website_url?: string;
  image_url?: string;
  product_id?: number;
  // §v3.inventory — stock defaults applied server-side when
  // omitted (0 / 10). Frontend ships them whenever the operator
  // fills the dedicated input fields in the Add drawer.
  stock_count?: number;
  low_stock_threshold?: number;
}

export async function listProducts() {
  return api<{ products: Product[]; count: number }>("/api/products");
}

// §v3.stock-forecast (2026-05-25) — AI re-order prediction.
export type ForecastStatus = "out_of_stock" | "reorder_now" | "low" | "healthy" | "no_sales";
export interface ForecastItem {
  product_id: number | string | null;
  name: string;
  sku: string;
  price: number | string | null;
  stock_count: number;
  low_stock_threshold: number;
  units_sold_window: number;
  avg_daily_sales: number;
  days_of_supply: number | null;
  stockout_date: string | null;
  reorder_point: number;
  suggested_reorder_qty: number;
  status: ForecastStatus;
}
export interface StockForecast {
  generated_at: string;
  params: { window_days: number; lead_time_days: number; safety_days: number; review_days: number };
  total_products: number;
  counts: Record<string, number>;
  reorder_list: Array<{ name: string; suggested_reorder_qty: number; days_of_supply: number | null; status: string }>;
  items: ForecastItem[];
  ai_summary: string;
}
export async function productForecast(opts?: {
  windowDays?: number;
  leadTimeDays?: number;
  ai?: boolean;
}): Promise<StockForecast> {
  const p = new URLSearchParams();
  if (opts?.windowDays) p.set("window_days", String(opts.windowDays));
  if (opts?.leadTimeDays) p.set("lead_time_days", String(opts.leadTimeDays));
  if (opts?.ai === false) p.set("ai", "0");
  const qs = p.toString();
  return api<StockForecast>(`/api/products/forecast${qs ? "?" + qs : ""}`);
}

export async function createProduct(payload: ProductCreate) {
  return api<{ ok: boolean; product_id: number; product: Product }>(
    "/api/products",
    { method: "POST", body: payload },
  );
}

export async function updateProduct(productId: string | number, patch: ProductUpdate) {
  return api<{ ok: boolean; product_id: number; updated_fields: string[] }>(
    `/api/products/${productId}`,
    {
      method: "PUT",
      body: patch,
    },
  );
}

export async function deleteProduct(productId: string | number) {
  return api<{ ok: boolean }>(`/api/products/${productId}`, { method: "DELETE" });
}

/** §v3.product-image-upload (2026-05-05) — upload one image
 *  for a single SKU. Stored under data/product_uploads/{pid}/
 *  and the products.csv image_url is updated atomically. */
export async function uploadProductImage(
  productId: string | number,
  file: File,
): Promise<{ ok: boolean; product_id: string; image_url: string; product: Product }> {
  const fd = new FormData();
  fd.append("file", file);
  return api<{ ok: boolean; product_id: string; image_url: string; product: Product }>(
    `/api/products/${productId}/image-upload`,
    { method: "POST", body: fd },
  );
}

export async function clearProductImage(productId: string | number) {
  return api<{ ok: boolean; product_id: string }>(
    `/api/products/${productId}/image`,
    { method: "DELETE" },
  );
}

// ─── §v3.product-gallery (2026-05-11) ──────────────────────────
// Multi-image gallery CRUD. ``uploadProductImage`` (above) keeps
// "append + set primary" semantics for the existing card drop
// zone; ``appendProductImage`` is the new "add to gallery without
// promoting" path used by the gallery strip in the edit drawer.

export async function listProductImages(productId: string | number) {
  return api<{ ok: boolean; product_id: string; images: ProductImage[] }>(
    `/api/products/${productId}/images`,
  );
}

/** Append one file to the gallery without promoting it to primary.
 *  When the gallery was empty the server still flips the new
 *  entry's primary flag — operator can always retag via
 *  ``replaceProductGallery``. */
export async function appendProductImage(
  productId: string | number,
  file: File,
  alt: string = "",
): Promise<{ ok: boolean; product_id: string; images: ProductImage[] }> {
  const fd = new FormData();
  fd.append("file", file);
  const query = alt ? `?alt=${encodeURIComponent(alt)}` : "";
  return api<{ ok: boolean; product_id: string; images: ProductImage[] }>(
    `/api/products/${productId}/images${query}`,
    { method: "POST", body: fd },
  );
}

/** Bulk-replace the gallery (used after a reorder, alt-text
 *  edit, or "make this primary" click). The server re-normalises
 *  the array — exactly one entry ends up primary, ``sort_order``
 *  re-stamped from iteration order. */
export async function replaceProductGallery(
  productId: string | number,
  images: ProductImage[],
) {
  return api<{ ok: boolean; product_id: string; images: ProductImage[] }>(
    `/api/products/${productId}/images`,
    { method: "PUT", body: { images } },
  );
}

/** Remove one entry from the gallery by index or URL. When neither
 *  is provided, clears the whole gallery (matches the
 *  ``DELETE /image`` legacy behaviour for the new model). */
export async function removeProductImage(
  productId: string | number,
  opts: { index?: number; url?: string } = {},
) {
  const params = new URLSearchParams();
  if (typeof opts.index === "number") params.set("index", String(opts.index));
  if (opts.url) params.set("url", opts.url);
  const query = params.toString() ? `?${params.toString()}` : "";
  return api<{ ok: boolean; product_id: string; images: ProductImage[] }>(
    `/api/products/${productId}/images${query}`,
    { method: "DELETE" },
  );
}

/** Bulk upload — match files to SKUs by normalised filename.
 *  Returns matched (auto-applied) + unmatched (operator picks
 *  manually) + skipped (rejected by validation). */
export interface BulkImageMatch {
  filename: string;
  product_id: string;
  product_name: string;
  image_url: string;
}

export interface BulkImageUnmatched {
  filename: string;
  candidates: string[];
  reason: string;
}

export interface BulkImageSkipped {
  filename: string;
  reason: string;
}

export async function bulkUploadProductImages(
  files: File[],
): Promise<{
  ok: boolean;
  applied: number;
  matched: BulkImageMatch[];
  unmatched: BulkImageUnmatched[];
  skipped: BulkImageSkipped[];
}> {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  return api<{
    ok: boolean;
    applied: number;
    matched: BulkImageMatch[];
    unmatched: BulkImageUnmatched[];
    skipped: BulkImageSkipped[];
  }>(
    `/api/products/images/bulk-upload`,
    { method: "POST", body: fd },
  );
}

// §v3.kb-scrape (2026-05-08) — auto-import products from a brand
// website. Pipeline runs server-side: sitemap.xml → JSON-LD →
// meta-tags → Grok LLM fallback. Returns a preview list (no DB
// write); operator commits via ``commitScrapedProducts`` after
// reviewing.
export interface ScrapedProductPreview {
  product_name: string;
  category: string;
  product_type: string;
  price_aed: string;
  net_weight: string;
  description_short: string;
  website_url: string;
  image_url: string;
  /** Which extraction path produced this row. */
  source: "jsonld" | "meta" | "llm" | "unknown";
  /** Coarse quality signal — surfaces as a chip in the preview UI. */
  confidence: "high" | "medium" | "low";
  /** §v3.kb-scrape-rev2 — extra Schema.org fields surfaced when
   *  available (mostly from the JSON-LD extraction path; meta and
   *  LLM paths leave them empty). */
  brand?: string;
  sku?: string;
  /** Schema.org enum tail: "InStock" | "OutOfStock" | "" */
  availability?: string;
  /** ISO currency code from the offer, e.g. "USD" / "AED". Used so
   *  the preview UI can flag non-AED rows before commit. */
  currency?: string;
  /** §scrape-dedupe-v2 — this product already exists in the tenant's
   *  catalog (exact-normalized or fuzzy name match). The drawer
   *  default-unpicks these so a re-import can never duplicate. */
  already_exists?: boolean;
  existing_match?: string;
  existing_id?: string;
  match_kind?: "exact" | "fuzzy" | "prefix" | "";
  /** §scrape-full-data — full image gallery + long description. */
  images?: string[];
  description_long?: string;
  /** §detect-changes — site price differs from the catalog price. */
  price_changed?: boolean;
  catalog_price_aed?: string;
  /** §v3.inventory — optional starting stock when the scraper finds
   *  a quantity (Schema.org's ``inventoryLevel`` / Shopify's
   *  variant ``inventory_quantity``). Operator can still override
   *  before commit; absence means "default to 0 on server side". */
  stock_count?: number;
  /** §v3.kb-scrape-variants (2026-05-11) — surfaced from Shopify
   *  ``variants[]`` or Schema.org ``hasVariant``. Empty / absent
   *  when the product ships in only one size. */
  variants?: ProductVariant[];
}

export interface ScrapeBrandResponse {
  ok: boolean;
  url: string;
  products: ScrapedProductPreview[];
  stats: {
    sitemap_urls_found?: number;
    listing_urls_found?: number;
    urls_to_scrape?: number;
    method_counts?: { jsonld?: number; meta?: number; llm?: number; skipped?: number };
    llm_calls_used?: number;
    failed_count?: number;
    elapsed_seconds?: number;
  };
  errors: string[];
}

export async function scrapeBrandProducts(
  url: string,
  opts: { max_products?: number; use_llm_fallback?: boolean } = {},
): Promise<ScrapeBrandResponse> {
  return api<ScrapeBrandResponse>("/api/products/scrape", {
    method: "POST",
    body: { url, ...opts },
  });
}

// §scrape-progress (2026-06-24) — the website scrape now runs as a background
// job so the UI shows real progress + ETA instead of one blocking request.
// startScrapeJob → {job_id}; poll scrapeJobStatus(job_id) until `done`, then read
// `result` (same shape as the old blocking ScrapeBrandResponse).
export interface ScrapeJobStatus {
  ok: boolean;
  job_id: string;
  phase: "starting" | "discovering" | "fetching" | "done" | "error";
  urls_total: number;
  urls_done: number;
  products_found: number;
  elapsed_seconds: number;
  done: boolean;
  error: string | null;
  result?: ScrapeBrandResponse;
}

export async function startScrapeJob(
  url: string,
  opts: { max_products?: number; use_llm_fallback?: boolean } = {},
): Promise<{ ok: boolean; job_id: string }> {
  return api<{ ok: boolean; job_id: string }>("/api/products/scrape/start", {
    method: "POST",
    body: { url, ...opts },
  });
}

export async function scrapeJobStatus(jobId: string): Promise<ScrapeJobStatus> {
  return api<ScrapeJobStatus>(
    `/api/products/scrape/status/${encodeURIComponent(jobId)}`,
  );
}

export interface CommitScrapedResponse {
  ok: boolean;
  added: Array<{ product_id: number; product_name: string }>;
  skipped: Array<{
    product_name: string;
    reason: string;
    existing_id?: string | number;
  }>;
  added_count: number;
  skipped_count: number;
}

/** §detect-changes — apply operator-approved in-place price updates. */
export async function applyScrapeUpdates(
  updates: Array<{ existing_id: string; price_aed: string }>,
): Promise<{ ok: boolean; applied: Array<{ product_id: string; product_name: string; old_price_aed: string; new_price_aed: string }> }> {
  return api("/api/products/scrape/apply-updates", {
    method: "POST",
    body: { updates },
  });
}

export async function commitScrapedProducts(
  products: ScrapedProductPreview[],
): Promise<CommitScrapedResponse> {
  return api<CommitScrapedResponse>("/api/products/scrape/commit", {
    method: "POST",
    body: { products },
  });
}

// §discounts (2026-07-13) — operator-managed discounts, offers & coupons.
// code === "" ⇒ automatic offer; code set ⇒ coupon (customer must give it).
// The MATH runs server-side at order placement; the announce toggle gates
// the offers block injected into the agent's chat + voice prompts.
export interface Discount {
  id: string;
  title: string;
  code: string;
  kind: "percent" | "fixed";
  value: number;
  scope: "all" | "products";
  product_ids: string[];
  min_order_aed: number;
  starts_at: number | null;
  expires_at: number | null;
  active: boolean;
  created_at: number;
  updated_at: number;
}

export interface DiscountsResponse {
  ok: boolean;
  discounts: Discount[];
  announce: boolean;
}

export async function listDiscounts(): Promise<DiscountsResponse> {
  return api<DiscountsResponse>("/api/products/discounts");
}

export async function createDiscount(payload: {
  title: string;
  kind: "percent" | "fixed";
  value: number;
  code?: string;
  min_order_aed?: number;
  starts_at?: number | null;
  expires_at?: number | null;
  active?: boolean;
}): Promise<{ ok: boolean; discount: Discount }> {
  return api("/api/products/discounts", { method: "POST", body: payload });
}

export async function updateDiscount(
  id: string,
  patch: Partial<Omit<Discount, "id" | "created_at" | "updated_at">>,
): Promise<{ ok: boolean; discount: Discount }> {
  return api(`/api/products/discounts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export async function deleteDiscount(id: string): Promise<{ ok: boolean }> {
  return api(`/api/products/discounts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function setDiscountsAnnounce(
  announce: boolean,
): Promise<{ ok: boolean; announce: boolean }> {
  return api("/api/products/discounts/announce", {
    method: "POST",
    body: { announce },
  });
}
